import { CommonModule } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  signal,
  effect,
  HostListener,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Subject, takeUntil } from 'rxjs';
import { fadeInOutAnimation, slideInOutAnimation } from '../../animations/angular-animations/fade';
import { PalantirButtonComponent } from '../button/button.component';
import { PalantirIconComponent } from '../icon/icon.component';
import { PalantirProgressComponent } from '../progress-bar/progress-bar.component';
import { PalantirTextComponent } from '../text/text.component';
import { PalantirTooltipModule } from '../tooltip/tooltip.module';
import { toastLogger } from './logger.instance';
import { PalantirToastService } from './toast.service';
import type {
  PalantirToastCard,
  PalantirToastCardEmitInfo,
  PalantirToastCardInfo,
  PalantirToastPosition,
} from './toast.type';
import { PalantirToastComponentThemes } from './toast.type';

const logger = toastLogger.extend('component');

interface EstimatedTimeReport {
  startTime?: number;
}

@Component({
  selector: 'palantir-toast',
  templateUrl: './toast.component.html',
  styleUrls: ['./toast.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [slideInOutAnimation, fadeInOutAnimation],
  standalone: true,
  imports: [
    CommonModule,
    PalantirIconComponent,
    PalantirProgressComponent,
    PalantirButtonComponent,
    PalantirTextComponent,
    PalantirTooltipModule,
  ],
})
export class PalantirToastComponent implements OnInit, OnDestroy {
  public themes = PalantirToastComponentThemes;

  public toasts = signal<Record<PalantirToastPosition, PalantirToastCard[]>>({
    'top-right': [],
    'top-center': [],
    'top-left': [],
    'bottom-right': [],
    'bottom-center': [],
    'bottom-left': [],
  });

  private readonly computeToastPositions = () =>
    Object.keys(this.toasts()) as PalantirToastPosition[];

  public toastPositions = computed(this.computeToastPositions);

  public timeouts = new Map<string, ReturnType<typeof setTimeout>>();

  public estimatedTimeReportMap = new Map<string, EstimatedTimeReport>();

  private unsubscribeAll$ = new Subject<void>();

  private prefersReducedMotion =
    typeof window === 'undefined' ? false : window.matchMedia('(prefers-reduced-motion: reduce)');

  public constructor(
    private readonly palantirToastService: PalantirToastService,
    private readonly sanitizer: DomSanitizer,
  ) {
    effect(() => {
      for (const position of this.toastPositions()) {
        const toasts = this.toasts()[position];
        for (const toast of toasts) {
          if (toast.isToastRemoved()) {
            this.removeToastFromDom(toast);
          }
        }
      }
    });
  }

  @HostListener('window:keydown.escape', ['$event'])
  public onEscKey(event: Event): void {
    event.preventDefault();
    this.resetHandler();
  }

  public ngOnInit(): void {
    this.palantirToastService.toastEmitter$
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe((data) => {
        const alreadyExistingToast = this.findToast(data.id);

        if (alreadyExistingToast) {
          this.updateHandler(alreadyExistingToast, data);
          return;
        }

        this.showHandler(data);
      });

    this.palantirToastService.closeEmitter$
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe((id) => {
        const toast = this.findToast(id);
        if (!toast) {
          return;
        }

        this.removeHandler(toast);
      });

    this.palantirToastService.clearAllEmitter$
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe(() => {
        this.resetHandler();
      });
  }

  public ngOnDestroy(): void {
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }

  private showHandler(data: PalantirToastCardEmitInfo): void {
    const toast: PalantirToastCard = {
      id: signal(data.id),
      message: signal(
        data.messageIsHtml
          ? (this.sanitizer.bypassSecurityTrustHtml(data.message) as string)
          : data.message,
      ),
      theme: signal(data.theme ?? 'info'),
      timeout: signal(data.timeout ?? 5_000),
      animationState: signal(
        typeof this.prefersReducedMotion !== 'boolean' && this.prefersReducedMotion.matches
          ? 'stable'
          : 'enter',
      ),
      progress: signal(
        data.progress === 'indeterminate'
          ? 'indeterminate'
          : typeof data.progress === 'number'
            ? Math.min(Math.max(data.progress, 1), 100)
            : null,
      ),
      estimatedTime: signal<string | null>(null),
      callToAction: signal(data.callToAction ?? null),
      callToActionLoading: signal(false),
      dismissible: signal(data.dismissible ?? true),
      icon: signal(data.icon ?? null),
      pauseOnHover: signal(data.pauseOnHover ?? true),
      messageIsHtml: signal(data.messageIsHtml ?? false),
      position: signal(data.position ?? 'bottom-left'),
      animation: signal(data.animation ?? 'slide'),
      swipeToDismiss: signal(data.swipeToDismiss ?? true),
      isToastHovered: signal(false),
      isToastRemoved: signal(false),
    };

    this.toasts.update((toasts) => {
      const position = toast.position();
      return {
        ...toasts,
        [position]: [...toasts[position], toast],
      };
    });

    if (toast.timeout()) {
      const timeout = setTimeout(() => this.removeHandler(toast), toast.timeout());
      this.timeouts.set(toast.id(), timeout);
    }
  }

  public updateHandler(
    card: PalantirToastCard,
    data: Partial<Omit<PalantirToastCardInfo, 'id'>>,
  ): void {
    if (data.message) {
      card.message.set(
        data.messageIsHtml
          ? (this.sanitizer.bypassSecurityTrustHtml(data.message) as string)
          : data.message,
      );
    }

    if (data.theme) {
      card.theme.set(data.theme);
    }

    if (data.timeout) {
      clearTimeout(this.timeouts.get(card.id()));
      card.timeout.set(data.timeout);
      const timeout = setTimeout(() => this.removeHandler(card), data.timeout);
      this.timeouts.set(card.id(), timeout);
    }

    card.callToAction.set(data.callToAction ?? null);
    card.dismissible.set(data.dismissible ?? true);
    card.icon.set(data.icon ?? null);
    card.pauseOnHover.set(data.pauseOnHover ?? true);
    card.messageIsHtml.set(data.messageIsHtml ?? false);

    if (card.position() !== data.position && data.position) {
      this.toasts.update((toasts) => {
        const oldPosition = card.position();
        const newPosition = data.position as PalantirToastPosition;
        const newToasts = { ...toasts };
        newToasts[oldPosition] = newToasts[oldPosition].filter(
          (toastItem) => toastItem.id() !== card.id(),
        );
        newToasts[newPosition] = [...newToasts[newPosition], card];
        return newToasts;
      });
      card.position.set(data.position);
    }

    if (data.animation) {
      card.animation.set(data.animation);
    }

    if (data.swipeToDismiss) {
      card.swipeToDismiss.set(data.swipeToDismiss);
    }

    if (data.progress === 'indeterminate') {
      card.progress.set('indeterminate');
      card.estimatedTime.set(null);
      return;
    }

    if (typeof data.progress !== 'number') {
      card.progress.set(null);
      return;
    }

    card.progress.set(Math.min(Math.max(data.progress, 1), 100));
    this.calculateProgressEstimatedTime(card);
  }

  public animationHandler(toast: PalantirToastCard): void {
    if (typeof this.prefersReducedMotion !== 'boolean' && this.prefersReducedMotion.matches) {
      return;
    }

    if (toast.animationState() === 'enter') {
      toast.animationState.set('stable');
    } else if (toast.animationState() === 'leave') {
      this.removeToastFromDom(toast);
    }
  }

  public onMouseEnter(toast: PalantirToastCard): void {
    if (!toast.pauseOnHover()) {
      return;
    }

    toast.isToastHovered.set(true);
    clearTimeout(this.timeouts.get(toast.id()));
  }

  public onMouseLeave(toast: PalantirToastCard): void {
    if (!toast.pauseOnHover()) {
      return;
    }

    toast.isToastHovered.set(false);
    const timeout = setTimeout(() => this.removeHandler(toast), toast.timeout());
    this.timeouts.set(toast.id(), timeout);
  }

  public removeHandler(toast: PalantirToastCard): void {
    if (
      (typeof this.prefersReducedMotion !== 'boolean' && this.prefersReducedMotion.matches) ||
      toast.animationState() === 'leave'
    ) {
      this.removeToastFromDom(toast);
    } else if (toast.animationState() === 'enter' || toast.animationState() === 'stable') {
      toast.animationState.set('leave');
    }
  }

  private removeToastFromDom(toast: PalantirToastCard): void {
    this.toasts.update((toasts) => {
      const position = toast.position();
      const newToasts = { ...toasts };
      newToasts[position] = newToasts[position].filter(
        (toastItem) => toastItem.id() !== toast.id(),
      );
      return newToasts;
    });
  }

  public trackByFn(_: number, toast: PalantirToastCard): string {
    return toast.id();
  }

  public resetHandler(): void {
    for (const timeout of this.timeouts.values()) {
      clearTimeout(timeout);
    }

    for (const position of this.toastPositions()) {
      for (const toast of this.toasts()[position]) {
        this.removeHandler(toast);
      }
    }
  }

  public async executeHandler(toast: PalantirToastCard, event: Event): Promise<void> {
    const callToAction = toast.callToAction();
    if (!callToAction?.action) {
      return;
    }

    if (toast.callToActionLoading()) {
      logger.warn('Toast action is already in progress, ignoring the new action request.');
      return;
    }

    try {
      toast.callToActionLoading.set(true);
      await callToAction.action(toast, event);
      toast.callToActionLoading.set(false);
    } catch (error) {
      logger.error('Error executing toast action:', error);
      toast.callToActionLoading.set(false);
    }

    if (!callToAction.closeAfterAction) {
      return;
    }

    this.removeHandler(toast);
  }

  private calculateProgressEstimatedTime(alert: PalantirToastCard): void {
    // If there's no progress, exit the function
    if (!alert.progress?.() || alert.progress() === 'indeterminate') {
      return;
    }

    if (!this.estimatedTimeReportMap.has(alert.id())) {
      alert.estimatedTime.set(null);
      this.estimatedTimeReportMap.set(alert.id(), {
        startTime: Date.now(),
      });
    }

    const estimatedTime = this.estimatedTimeReportMap.get(alert.id())!;

    const elapsed = (Date.now() - estimatedTime.startTime!) / 1_000; // Convert milliseconds to seconds

    if (elapsed <= 0) {
      return;
    }

    const alertProgress = alert.progress() as number;

    const progressPerSecond = alertProgress / elapsed;

    const estimateTimeRemaining = (100 - alertProgress) / progressPerSecond;

    const estimatedCompletionTime = new Date(Date.now() + estimateTimeRemaining * 1_000);
    const timeRemaining = Math.round((estimatedCompletionTime.getTime() - Date.now()) / 1_000);

    alert.estimatedTime?.set(this.formatTimeRemaining(timeRemaining));
  }

  private formatTimeRemaining(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }

    return `${remainingSeconds}s`;
  }

  public alertProgressAsNumber(progress: number | 'indeterminate' | null): number | null {
    return !progress || progress === 'indeterminate' ? null : progress;
  }

  public alertProgressAsString(progress: number | 'indeterminate' | null): 'indeterminate' | null {
    return !progress || progress === 'indeterminate' ? 'indeterminate' : null;
  }

  public getToastZIndex(index: number, toasts: PalantirToastCard[]): number {
    return toasts.length - index;
  }

  private findToast(id: string): PalantirToastCard | undefined {
    for (const position of this.toastPositions()) {
      const toast = this.toasts()[position].find((toastItem) => toastItem.id() === id);
      if (toast) {
        return toast;
      }
    }

    return undefined;
  }

  // Swipe to dismiss
  private swipeState = {
    startX: 0,
    startY: 0,
    isSwiping: false,
  };

  public onTouchStart(event: TouchEvent, toast: PalantirToastCard): void {
    if (!toast.swipeToDismiss()) {
      return;
    }

    this.swipeState.startX = event.touches[0].clientX;
    this.swipeState.startY = event.touches[0].clientY;
    this.swipeState.isSwiping = true;

    if (toast.pauseOnHover()) {
      clearTimeout(this.timeouts.get(toast.id()));
    }
  }

  public onTouchMove(event: TouchEvent, toast: PalantirToastCard): void {
    if (!this.swipeState.isSwiping || !toast.swipeToDismiss()) {
      return;
    }

    const deltaX = event.touches[0].clientX - this.swipeState.startX;
    const deltaY = event.touches[0].clientY - this.swipeState.startY;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
      // eslint-disable-next-line unicorn/prefer-query-selector
      const toastElement = document.getElementById(toast.id());
      if (toastElement) {
        toastElement.style.transform = `translateX(${deltaX}px)`;
      }
    }
  }

  public onTouchEnd(event: TouchEvent, toast: PalantirToastCard): void {
    if (!this.swipeState.isSwiping || !toast.swipeToDismiss()) {
      return;
    }

    const deltaX = event.changedTouches[0].clientX - this.swipeState.startX;
    if (Math.abs(deltaX) > 100) {
      this.removeHandler(toast);
    } else {
      // eslint-disable-next-line unicorn/prefer-query-selector
      const toastElement = document.getElementById(toast.id());
      if (toastElement) {
        toastElement.style.transform = 'translateX(0)';
      }

      if (toast.pauseOnHover()) {
        const timeout = setTimeout(() => this.removeHandler(toast), toast.timeout());
        this.timeouts.set(toast.id(), timeout);
      }
    }

    this.swipeState.isSwiping = false;
  }
}
